import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { useScope } from "@/context/ScopeContext";
import { toast } from "sonner";
import { getBackendUrl } from "@/lib/backend";
import MistralGuidanceChip from "@/components/MistralGuidanceChip";
import { useMistralNarrativeOverlay } from "@/hooks/useMistralNarrativeOverlay";
import { USM_TABS } from "@/utils/unifiedSchoolManagementData";
import { buildUsmDerived, buildUsmMarkdown } from "@/utils/unifiedSchoolManagementDerived";
import { bulletsToUsmActionQueue } from "@/utils/mistralInsightMappers";
import {
  OverviewScreen,
  ManagementMixScreen,
  ShareScreen,
  SchoolsScreen,
  EnrolmentScreen,
  TeachersScreen,
  PtrScreen,
  DrilldownScreen,
  BifurcationScreen,
  ActionsScreen,
  QualityScreen,
} from "@/components/UnifiedSchoolManagementScreens";
import "./SchoolTypeBifurcationDashboard.css";

const BACKEND_URL = getBackendUrl();
const API = `${BACKEND_URL}/api`;

const SCREEN_COMPONENTS = {
  overview: OverviewScreen,
  mix: ManagementMixScreen,
  share: ShareScreen,
  schools: SchoolsScreen,
  enrolment: EnrolmentScreen,
  teachers: TeachersScreen,
  ptr: PtrScreen,
  drilldown: DrilldownScreen,
  bifurcation: BifurcationScreen,
  actions: ActionsScreen,
  quality: QualityScreen,
};

const SchoolTypeBifurcationDashboard = () => {
  const { scope } = useScope();
  const [activeTab, setActiveTab] = useState("overview");
  const [loading, setLoading] = useState(true);
  const [apiPayload, setApiPayload] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { district_name: "PUNE" };
      if (scope.districtCode) params.district_code = scope.districtCode;
      if (scope.blockCode) params.block_code = scope.blockCode;
      if (scope.udiseCode) params.udise_code = scope.udiseCode;
      if (scope.districtName) params.district_name = scope.districtName;
      if (scope.blockName) params.block_name = scope.blockName;
      if (scope.schoolName) params.school_name = scope.schoolName;

      const res = await axios.get(`${API}/enrolment/management-bifurcation`, { params });
      setApiPayload(res.data || null);
    } catch (error) {
      console.error("Error fetching management bifurcation:", error);
      toast.error("Failed to load management bifurcation data");
      setApiPayload(null);
    } finally {
      setLoading(false);
    }
  }, [
    scope.version,
    scope.districtCode,
    scope.blockCode,
    scope.udiseCode,
    scope.districtName,
    scope.blockName,
    scope.schoolName,
  ]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const derived = useMemo(() => buildUsmDerived(apiPayload), [apiPayload]);
  const scopeLabel = apiPayload?.scope_label || scope.districtName || "District scope";

  const buildLocalMarkdown = useCallback(
    () => buildUsmMarkdown(derived),
    [derived]
  );

  const scopeKey = useMemo(
    () =>
      JSON.stringify({
        schools: apiPayload?.grand_total?.schools,
        risks: derived?.risks?.length,
        tab: activeTab,
      }),
    [apiPayload?.grand_total?.schools, derived?.risks?.length, activeTab]
  );

  const {
    loading: aiLoading,
    pickFromBucket,
    pickMapped,
    provider: aiProvider,
    isMistral,
  } = useMistralNarrativeOverlay({
    kind: "unified-school-management",
    title: "Enrollment Analytics",
    buildLocalMarkdown,
    context: { derived, apiPayload },
    enabled: Boolean(derived),
    scopeKey,
  });

  const enrichedDerived = useMemo(() => {
    if (!derived) return null;

    const localStory = derived.hero.story;
    const localQueue = derived.actionQueue;
    const localReviewNote =
      "Use this unified dashboard as the single review screen for management structure, bifurcation health, teacher adequacy and data quality during district and state governance meetings.";

    const story = aiLoading ? localStory : pickFromBucket(localStory, "insights", 0) || localStory;
    const actionQueue = aiLoading
      ? localQueue
      : pickMapped(localQueue, "actions", bulletsToUsmActionQueue) || localQueue;
    const reviewNote = aiLoading
      ? localReviewNote
      : pickFromBucket(localReviewNote, "recs", 0) || localReviewNote;

    return {
      ...derived,
      hero: { ...derived.hero, story },
      actionQueue,
      reviewNote,
      aiLoading,
      isMistral,
    };
  }, [derived, aiLoading, pickFromBucket, pickMapped, isMistral]);

  const ActiveScreen = SCREEN_COMPONENTS[activeTab] || OverviewScreen;

  return (
    <div className="usm-dashboard-page animate-fade-in" data-testid="school-type-bifurcation-dashboard">
      <div className="usm-content">
        <section className="usm-header">
          <div>
            <div className="usm-eyebrow">Merged view · KPI coverage from both dashboards · 2025–26</div>
            <h1>Enrollment Analytics</h1>
            <p>
              This single dashboard merges the <b>School Management KPI Dashboard</b> and the{" "}
              <b>Managementwise Bifurcation Tabbed Dashboard</b> into one master experience. It covers executive
              KPIs, management mix, school/enrolment/teacher shares, PTR risk, teacher adequacy, detailed
              management-code drilldown, managementwise bifurcation table, and data quality alerts.
            </p>
          </div>
          <div className="usm-header-actions">
            {derived ? (
              <MistralGuidanceChip
                provider={aiProvider}
                loading={aiLoading}
                className="usm-ai-chip"
              />
            ) : null}
            <button type="button" className="usm-chip-btn" onClick={() => toast.info("Export coming soon")}>
              Export
            </button>
            <button type="button" className="usm-chip-btn primary" onClick={fetchData}>
              Refresh
            </button>
          </div>
        </section>

        <div className="usm-tabs-wrap">
          <nav className="usm-tabs" aria-label="Enrollment analytics tabs">
            {USM_TABS.map((tab) => (
              <button
                key={tab.id}
                type="button"
                className={`usm-tab ${activeTab === tab.id ? "active" : ""}`.trim()}
                onClick={() => {
                  setActiveTab(tab.id);
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {loading ? (
          <div className="usm-loading">
            <div className="loading-spinner" />
          </div>
        ) : !apiPayload ? (
          <section className="usm-screen">
            <div className="usm-empty">Unable to load management bifurcation data for the selected scope. Use Refresh to retry.</div>
          </section>
        ) : (
          <section className="usm-screen">
            <ActiveScreen apiPayload={apiPayload} derived={enrichedDerived} scopeLabel={scopeLabel} />
          </section>
        )}
      </div>
    </div>
  );
};

export default SchoolTypeBifurcationDashboard;
